import { inject, injectable } from 'inversify';
import React from 'react';

import { AxiosDataState, User } from '@project44/ui-utils';
import {
  DtoBase,
  Enums,
  TenantModels,
  UIDtoFactory,
} from '@project44-models/iam-gateway';

import { UserPrincipalAdapter } from '../../../external/UserPrincipal/infrastructure/adapters/UserPrincipal.adapter';
import { ComponentPropsValueObject } from '../../../shared/core/domain/ComponentPropsValueObject';
import { TAnyOfPermissionsHOC } from '../../../shared/core/infrastructrue/types';
import { TenantComponentFactory } from '../infrastructure/factories/Tenant.component.factory';
import { TenantHookFactory } from '../infrastructure/factories/Tenant.hook.factory';
import { TTenantSwitcher } from '../infrastructure/ui/TenantSwitcher/TenantSwitcher.view';
import { TTenantTileCard } from '../infrastructure/ui/TenantTileCard/TenantTileCard.view';

@injectable()
export class TenantUIService {
  constructor(
    @inject(TenantComponentFactory)
    private readonly tenantComponentFactory: TenantComponentFactory,

    @inject(UserPrincipalAdapter)
    private readonly userPrincipalAdapter: UserPrincipalAdapter,

    @inject(TenantHookFactory)
    private readonly tenantHookFactory: TenantHookFactory,
  ) {}

  public createTenantSwitcherComponent(
    payload: TAnyOfPermissionsHOC<{
      isFeatureFlagIam1057Enabled: boolean;
      toggleTenantSwitcherComponent?: () => void;
      isInView?: boolean;
    }>,
  ): React.ReactElement<TTenantSwitcher> {
    const UserTenantCard =
      this.createUserPrincipalTenantTileCardComponent(payload);

    return this.tenantComponentFactory.createTenantSwitcher(
      ComponentPropsValueObject.create({
        ...payload,
        useSwitchTenantHook:
          this.tenantHookFactory.composeUseSwitchTenantHook(),
        useTenantQueryHook: this.tenantHookFactory.composeUseTenantQueryHook(),
        UserPrincipalTenantTileCardComponent: UserTenantCard,
      }),
    );
  }

  public createUserMoveTenantSwitcherComponent(
    payload: TAnyOfPermissionsHOC<{
      user: User;
      moveUserAxiosDataState: AxiosDataState;
      tenantSelectorHandler: (options: {
        tenantDto: DtoBase<TenantModels.TenantViewModel>;
      }) => Promise<void>;
    }>,
  ) {
    return this.tenantComponentFactory.createMoveUserTenantSwitcher({
      ...payload,
      isFeatureFlagIam1057Enabled: true,
      withMaxWidth: true,
      useSwitchTenantHook: () => [
        payload.moveUserAxiosDataState,
        payload.tenantSelectorHandler,
      ],
      useTenantQueryHook: this.tenantHookFactory.composeUseTenantQueryHook(),
      UserPrincipalTenantTileCardComponent: React.createElement(() => null),
    });
  }

  public createTenantTileCardComponent(
    payload: TAnyOfPermissionsHOC<Omit<TTenantTileCard, 'config'>>,
  ): React.ReactElement<Record<string, unknown>> {
    return this.tenantComponentFactory.createTenantTileCard(payload);
  }

  public createUserPrincipalTenantTileCardComponent(
    payload: TAnyOfPermissionsHOC<
      Omit<
        TTenantTileCard,
        'config' | 'tenantDto' | 'isCurrentTenant' | 'isHighlightable'
      >
    >,
  ): React.ReactElement<Record<string, unknown>> {
    return this.tenantComponentFactory.createTenantTileCard({
      ...payload,
      isCurrentTenant: true,
      tenantDto: UIDtoFactory.create(
        Enums.UiDtoFactoryEnum.TENANT,
        this.userPrincipalAdapter.toTenant(payload.user),
      ),
    });
  }
}
